import React from "react";
import { useSearchParams } from "react-router-dom";

import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";
import { StaffRow } from "../components/staff-table";
import AddStaffModal from "../components/popup/add-staff";
import AddBtn from "../components/ui/add-btn";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import staffImage from '../files/images/staff.png';
import { useMediaQuery, useTheme, Button, Box, Fab } from "@mui/material";
import { Menu, Plus } from "lucide-react";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import routes from "../api/routes";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useToast } from "../providers/toaster-provider";
import DynamicTableVirtual from "../components/table-virtual";
import { Column } from "../components/table-virtual";

const formatCycle = (cycle: string, paidOn: string) => {
    if (!paidOn) return cycle;

    if (cycle === 'Weekly') {
        const day = paidOn.replace('weekly-', '');
        return `${cycle} (Every ${day})`;
    }
    
    if (cycle === 'Fortnightly') {
        const day = paidOn.replace('fortnightly-', '');
        return `${cycle} (Every ${day})`;
    }
    
    if (cycle === 'Monthly') {
        const date = paidOn.replace('monthly-', '');
        const suffix = getSuffix(parseInt(date));
        return `${cycle} (Every ${date}${suffix})`;
    }
    
    if (cycle === 'Quarterly') {
        const parts = paidOn.replace('quarterly-', '').split('-');
        if (parts.length === 2) {
            return `${cycle} (${parts[0]} - Every ${parts[1]})`;
        }
    }
    
    if (cycle === 'Half-yearly') {
        const parts = paidOn.replace('half-yearly-', '').split('-');
        if (parts.length === 2) {
            return `${cycle} (${parts[0]} - ${parts[1]})`;
        }
    }
    
    if (cycle === 'Yearly') {
        const parts = paidOn.replace('yearly-', '').split('-');
        if (parts.length === 2) {
            return `${cycle} (Every ${parts[1]} ${parts[0]})`;
        }
    }
    
    return cycle;
};

const getSuffix = (n: number): string => {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    const lastDigit = n % 10;
    return ['th', 'st', 'nd', 'rd'][lastDigit] || 'th';
};

const getCycleColor = (cycle: string): string => {
    const baseCycle = cycle.split(' ')[0]; // Get the base cycle (e.g., "Weekly" from "Weekly (Every Monday)")
    const colors: Record<string, string> = {
        'Weekly': '#3F51B5',
        'Fortnightly': '#2196F3',
        'Monthly': 'var(--color-primary)',
        'Quarterly': '#9C27B0',
        'Half-yearly': '#FF9800',
        'Yearly': '#4CAF50',
        'Ad-hoc': '#c7c7c7',
    };
    return colors[baseCycle] || '#F0F0F0';
}

const Staff: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    const canCreate = can('staff', 'create');
    const canUpdate = can('staff', 'update');
    const canDelete = can('staff', 'delete');
    const { showError, showSuccess } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [searchParams] = useSearchParams();
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedStaffId, setSelectedStaffId] = React.useState<number | null>(null);
    const [data, setData] = React.useState<StaffRow[]>([]);
    const [contributionColumns, setContributionColumns] = React.useState<Column[]>([]);
    const [processedData, setProcessedData] = React.useState<Record<string, any>[]>([]);
    const list = useListSearch(processedData);

    const [fetch, setFetch] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();
    const [create, setCreate] = useApi();

    const baseColumns: Column[] = [
        { id: 'name', label: 'Name', size: 180, type: 'string', mobilePrimary: true, mobileOrder: 1, mobileWidth: 110, mobileHideLabel: true },
        { id: 'role', label: 'Role', size: 120, type: 'string', mobilePrimary: true, mobileOrder: 2, mobileWidth: 90 },
        {
            id: 'paymentCycle',
            label: 'Payment Cycle',
            size: 140,
            type: 'custom',
            mobilePrimary: true,
            mobileOrder: 3,
            mobileWidth: 100,
            mobileHideLabel: true,
            customElement(value: any, row: Record<string, any>) {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            fontSize: '0.75rem',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <span style={{ 
                            width: 'fit-content',
                            backgroundColor: getCycleColor(value),
                            color: '#FFFFFF',
                            padding: '2px 12px',
                            borderRadius: '12px',
                        }}>{value}</span>
                    </Box>
                );
            }, 
        },
    ];

    const actionColumn: Column = {
        id: 'actions',
        label: 'Actions',
        type: 'actions',
        size: 75,
        onEdit: (row: Record<string, any>) => { 
            setSelectedStaffId(row.id);
            setOpenModal('edit'); 
        },
        onDelete: (row: Record<string, any>) => { 
            setSelectedStaffId(row.id);
            setOpenModal('delete'); 
        }
    };

    // Handle redirect from transaction detail for staff payment provisions
    React.useEffect(() => {
        const editStaffId = searchParams.get('editStaffId');
        if (editStaffId) {
            const staffId = parseInt(editStaffId, 10);
            if (!isNaN(staffId)) {
                setSelectedStaffId(staffId);
                setOpenModal('edit');
                // Clean up the URL
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('editStaffId');
                window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
            }
        }
    }, [searchParams]);

    // Generate contribution columns and process data
    React.useEffect(() => {
        if (data.length === 0) {
            setContributionColumns([]);
            setProcessedData([]);
            return;
        }

        // Get all unique contributions across all staff members
        const allContributions = new Set<string>();
        data.forEach(staff => {
            if (staff.contributions && Array.isArray(staff.contributions)) {
                staff.contributions.forEach((contrib: any) => {
                    if (contrib.name) {
                        allContributions.add(contrib.name);
                    }
                });
            }
        });

        // Create columns for each contribution
        const dynColumns: Column[] = Array.from(allContributions).map(contribName => ({
            id: `contrib_${contribName.replace(/\\s+/g, '_').toLowerCase()}`,
            label: contribName,
            type: 'currency',
            size: 100
        }));

        setContributionColumns(dynColumns);

        // Process data to include contribution amounts
        const processed = data.map(staff => {
            const processedStaff: Record<string, any> = {
                id: staff.id,
                name: staff.name,
                role: staff.role,
                paymentCycle: staff.paymentCycle
            };

            // Initialize all contributions to 0
            allContributions.forEach(contribName => {
                const key = `contrib_${contribName.replace(/\\s+/g, '_').toLowerCase()}`;
                processedStaff[key] = 0;
            });

            // Set actual contribution amounts
            if (staff.contributions && Array.isArray(staff.contributions)) {
                staff.contributions.forEach((contrib: any) => {
                    if (contrib.name && contrib.amount !== undefined) {
                        const key = `contrib_${contrib.name.replace(/\\s+/g, '_').toLowerCase()}`;
                        processedStaff[key] = contrib.amount;
                    }
                });
            }

            return processedStaff;
        });

        setProcessedData(processed);
    }, [data]);

    // Combine all columns
    const allColumns = [...baseColumns, ...contributionColumns, actionColumn];

    React.useEffect(() => {
        document.title = "FineAnts | Staff Management";
    }, []);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.staff.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetch({ url, method });
    }, [user, create, update, remove, setFetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (!isLoading && data) {
            setData(data.map((item: any) => ({
                id: item.id,
                name: item.name,
                role: item.role,
                paymentCycle: formatCycle(item.paymentCycle, item.paidOn),
                contributions: item.appliedContributionBreakdown,
            })));
        }
        if (error) {
            showError(typeof error === "string" ? error : "Failed to fetch staff data.");
        }
    }, [fetch, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (!isLoading && data) {
            showSuccess("Staff created successfully.");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === "string" ? error : "Failed to create staff.");
        }
    }, [create, showSuccess, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (!isLoading && data) {
            showSuccess("Staff updated successfully.");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === "string" ? error : "Failed to update staff.");
        }
    }, [update, showSuccess, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (!isLoading && data) {
            showSuccess("Staff removed successfully.");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === "string" ? error : "Failed to remove staff.");
        }
    }, [remove, showSuccess, showError]);

    const handleDeleteStaff = () => {
        if (!selectedStaffId) return;
        const { url, method } = buildApiRequestConfig(routes.staff.delete, [{ param: 'id', value: selectedStaffId }]);
        setRemove({ url, method });
    };

    const handleUpdateStaff = (staff: Omit<StaffRow, 'id'>) => {
        if (!selectedStaffId) return;
        const { url, method } = buildApiRequestConfig(routes.staff.update, [{ param: 'id', value: selectedStaffId }]);
        setUpdate({ url, method, body: {
            ...staff,
            entityId: user?.entityId,
        } });
    };

    const handleCreateStaff = (staff: Omit<StaffRow, 'id'>) => {
        const { url, method } = buildApiRequestConfig(routes.staff.create);
        setCreate({ url, method, body: {
            ...staff,
            entityId: user?.entityId,
        } });
    };

    return (
        <div style={{ width: '100%' }}>
            <AddStaffModal
                open={openModal === 'add' || openModal === 'edit'}
                isEdit={{ state: openModal === 'edit', id: selectedStaffId }}
                onClose={() => setOpenModal('')}
                onConfirm={openModal === 'add' ? handleCreateStaff : handleUpdateStaff}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Staff?"
                description="This action will permanently remove the selected staff."
                image={{ src: staffImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={handleDeleteStaff}
            />
            <Topbar
                title={<ListTitle title="Staff Payroll" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                    {!isMobile && (
                        <AddBtn disabled={!canCreate} onClick={() => setOpenModal('add')} label="Add New Staff" />
                    )}
                </Box>
            </Topbar>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    data={list.filtered}
                    columns={allColumns}
                    isLoading={fetch.isLoading}
                    scrollMode="page"
                    canEditRows={canUpdate}
                    canDeleteRows={canDelete}
                />
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreate && (
                <Fab
                    onClick={() => setOpenModal('add')}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default Staff;